import { Component, Input, Output, EventEmitter, OnInit, OnDestroy, OnChanges, SimpleChanges, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { Subscription } from 'rxjs';
import { Fpc } from 'src/app/models/fpc';
import { CategoriesService } from 'src/app/services/categories.service';

interface FpcListItem {
  fpcCode: string;
  codeDisplay: string;
  name: string;
}

@Component({
  selector: 'app-category-selector',
  templateUrl: './category-selector.component.html',
  styleUrl: './category-selector.component.scss',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    InputTextModule,
    ButtonModule,
    TooltipModule,
  ]
})
export class CategorySelectorComponent implements OnInit, OnChanges, OnDestroy {
  @Input() visible = false;
  @Input() selectedFpcs: Fpc[] = [];
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() onConfirm = new EventEmitter<Fpc[]>();

  private _allItems: FpcListItem[] = [];
  searchTerm = '';

  private pendingCodesSignal = signal<Set<string>>(new Set());
  readonly pendingCodes = computed(() => this.pendingCodesSignal());

  private subscription = new Subscription();

  constructor(private categoriesService: CategoriesService) {}

  readonly pageSize = 10;
  currentPage = 0;

  get filteredItems(): FpcListItem[] {
    const term = this.searchTerm.toLowerCase();
    if (!term) return this._allItems;
    return this._allItems.filter(item =>
      item.codeDisplay.toLowerCase().includes(term) ||
      item.name.toLowerCase().includes(term)
    );
  }

  get pagedItems(): FpcListItem[] {
    const start = this.currentPage * this.pageSize;
    return this.filteredItems.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredItems.length / this.pageSize);
  }

  onSearchChange(): void {
    this.currentPage = 0;
  }

  ngOnInit(): void {
    this.pendingCodesSignal.set(new Set(this.selectedFpcs.map(f => f.fpcCode)));

    this.subscription = this.categoriesService.getFpcFamilies$().subscribe(families => {
      this._allItems = families.flatMap(fam =>
        (fam.variants ?? []).map(v => ({
          fpcCode: v.fpcCode,
          codeDisplay: v.fpcCode.replace(/^FPC/i, ''),
          name: v.name,
        }))
      );
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['visible']?.currentValue === true && !changes['visible'].previousValue) {
      this.pendingCodesSignal.set(new Set(this.selectedFpcs.map(f => f.fpcCode)));
      this.searchTerm = '';
      this.currentPage = 0;
    }
  }

  isPending(fpcCode: string): boolean {
    return this.pendingCodes().has(fpcCode);
  }

  toggleItem(item: FpcListItem): void {
    const next = new Set(this.pendingCodes());
    if (next.has(item.fpcCode)) {
      next.delete(item.fpcCode);
    } else {
      next.add(item.fpcCode);
    }
    this.pendingCodesSignal.set(next);
  }

  confirm(): void {
    const itemMap = new Map(this._allItems.map(i => [i.fpcCode, i]));
    const fpcs: Fpc[] = [...this.pendingCodes()]
      .map(code => {
        const item = itemMap.get(code);
        return item ? { fpcCode: item.fpcCode, name: item.name } : null;
      })
      .filter(Boolean) as Fpc[];
    this.onConfirm.emit(fpcs);
    this.close();
  }

  close(): void {
    this.visible = false;
    this.visibleChange.emit(false);
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }
}
